import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";

const STATUS = [
  { id: "requested", label: "Requested" },
  { id: "in_review", label: "In review" },
  { id: "published", label: "Published" },
  { id: "declined", label: "Declined" },
];

async function download(id, slug) {
  const { data } = await api.get(`/platform/app-live-requests/${id}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `app-live-${slug || id}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function SalesAppLive() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);

  const load = async () => {
    const { data } = await api.get("/platform/app-live-requests");
    setItems(data.items || []);
  };
  useEffect(() => { load().catch((e) => toast.error(extractErr(e))); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/platform/app-live-requests/${id}/status`, { status });
      await load();
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  if (!items) {
    return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading requests…</div>;
  }

  return (
    <div className="space-y-6" data-testid="app-live-requests">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-100">App Live Request</h1>
        <p className="text-slate-400 text-sm mt-1">Merchants who signed the publishing-risk agreement and asked to go live on the Mera Startup developer account.</p>
      </div>
      {items.length === 0 ? (
        <Card className="p-8 text-slate-400 bg-slate-900 border-slate-800" data-testid="app-live-empty">No requests yet.</Card>
      ) : (
        <div className="space-y-3">
          {items.map((row) => (
            <Card key={row.id} className="p-4 bg-slate-900 border-slate-800 text-slate-100" data-testid={`app-live-${row.slug || row.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-semibold">{row.store_name || "Store"} <span className="text-slate-400 font-normal">· {row.slug}</span></div>
                  <div className="text-sm text-slate-300 mt-1">{row.app_name}</div>
                  <div className="text-xs text-slate-400 mt-1">{row.merchant_name || "Merchant"} · {row.email}{row.phone ? ` · ${row.phone}` : ""} · {row.plan || "plan n/a"}</div>
                  <div className="text-xs text-slate-500 mt-1">Signed by {row.signatory_name} · {row.agreement?.signature?.signed_at || row.signed_at}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    className="bg-slate-800 border border-slate-700 rounded-md text-sm px-2 py-1.5"
                    data-testid={`app-live-status-${row.id}`}
                    value={row.status || "requested"}
                    onChange={(e) => setStatus(row.id, e.target.value)}
                  >
                    {STATUS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                  <Button type="button" variant="outline" className="border-slate-600 text-slate-100" data-testid={`app-live-pdf-${row.id}`} onClick={() => download(row.id, row.slug).catch((e) => toast.error(extractErr(e)))}>
                    <Download className="w-4 h-4 mr-1" /> PDF
                  </Button>
                  <Button type="button" variant="outline" className="border-slate-600 text-slate-100" onClick={() => setOpen(open === row.id ? null : row.id)}>
                    {open === row.id ? "Hide agreement" : "View agreement"}
                  </Button>
                </div>
              </div>
              {open === row.id && row.agreement && (
                <div className="mt-4 rounded-lg bg-white text-slate-900 p-5 text-sm space-y-3 max-h-[480px] overflow-auto" data-testid={`app-live-agreement-${row.id}`}>
                  <div className="text-[11px] tracking-[0.28em] text-slate-500">MERA STARTUP</div>
                  <div className="font-semibold">{row.agreement.title}</div>
                  <p>{row.agreement.warning}</p>
                  {(row.agreement.sections || []).map((section) => (
                    <div key={section.heading}>
                      <div className="font-semibold">{section.heading}</div>
                      {(section.paragraphs || []).map((p) => <p key={p.slice(0, 24)} className="mt-1">{p}</p>)}
                      <ul className="list-disc pl-5">{(section.bullets || []).map((b) => <li key={b.slice(0, 24)}>{b}</li>)}</ul>
                    </div>
                  ))}
                  <div>Store: {row.agreement.signature?.store_name}</div>
                  <div>App: {row.agreement.signature?.app_name}</div>
                  <div>Signatory: {row.agreement.signature?.signatory}</div>
                  <div>Accepted: {row.agreement.signature?.signed_at}</div>
                  <div>Stamp: {row.agreement.signature?.stamp}</div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
